
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { GalleryPhoto } from "./carousel/types";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { GalleryPdfExport } from "./GalleryPdfExport";
import { 
  PregnancyWeekSelector,
  PregnancyPhotoHeader,
  PregnancyPhotoDisplay
} from "./pregnancy";

interface PregnancyPhotoCollectionProps {
  userId?: string;
  isPremium?: boolean;
  isGuestMode?: boolean;
}

export function PregnancyPhotoCollection({ 
  userId, 
  isPremium = false,
  isGuestMode = false
}: PregnancyPhotoCollectionProps) {
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [weeklyPhotos, setWeeklyPhotos] = useState<GalleryPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();
  
  const fetchWeekPhotos = async (week: string) => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      // Get the week number for storage
      const weekNumber = week.match(/\d+/)?.[0] || "";
      const weekCategory = `pregnancy-week-${weekNumber}`;
      
      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .eq('user_id', userId)
        .eq('category', weekCategory);
        
      if (error) throw error;
      
      setWeeklyPhotos(data as GalleryPhoto[]);
    } catch (error) {
      console.error("Error fetching pregnancy photos:", error);
      toast.error("Failed to load pregnancy photos");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleWeekClick = (week: string) => {
    setSelectedWeek(week);
    if (!isGuestMode && userId) {
      fetchWeekPhotos(week);
    }
  };
  
  const handlePhotoUploaded = () => {
    if (selectedWeek) {
      fetchWeekPhotos(selectedWeek);
    }
  };
  
  const handlePhotoDeleted = () => {
    if (selectedWeek) {
      fetchWeekPhotos(selectedWeek);
    }
  };
  
  // Function to fetch all pregnancy photos for PDF export
  const fetchAllPregnancyPhotos = async () => {
    if (!userId) return [];
    
    try {
      const { data, error } = await supabase
        .from('gallery_photos')
        .select('*')
        .eq('user_id', userId)
        .ilike('category', 'pregnancy-%')
        .order('category', { ascending: true });
        
      if (error) throw error;
      
      return data as GalleryPhoto[];
    } catch (error) {
      console.error("Error fetching all pregnancy photos:", error);
      toast.error("Failed to load pregnancy photos for export");
      return [];
    }
  };
  
  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold">{t('generate.pregnancyJourney', language)}</h2>
        
        {!isGuestMode && userId && (
          <GalleryPdfExport 
            photos={[]} 
            collectionTitle={t('generate.pregnancyJourney', language)}
            fetchPhotos={fetchAllPregnancyPhotos}
          />
        )}
      </div>
      
      <PregnancyWeekSelector 
        selectedWeek={selectedWeek} 
        onWeekSelect={handleWeekClick} 
      />

      {selectedWeek && (
        <PregnancyPhotoHeader
          selectedWeek={selectedWeek}
          isPremium={isPremium}
          isGuestMode={isGuestMode}
          userId={userId}
          onPhotoUploaded={handlePhotoUploaded}
          weeklyPhotos={weeklyPhotos}
        />
      )}
      
      <PregnancyPhotoDisplay
        selectedWeek={selectedWeek}
        isLoading={isLoading}
        isGuestMode={isGuestMode}
        isPremium={isPremium}
        weeklyPhotos={weeklyPhotos}
        onPhotoDeleted={handlePhotoDeleted}
      />
    </div>
  );
}
